import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  bootstrapAssessmentSessionFromUrl,
  clearAuthToken,
  getAuthToken,
  getImitationUserId,
  hasPendingPreauthCodeInUrl,
} from "../lib/authToken";
import { AssessmentLoadingState } from "../components/AssessmentLoadingState";
import { getApiUrls } from "../lib/apiUrl";
import { fetchActualUserRole } from "../services/appUserService";

export type MeUser = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  grade: string;
  district_id?: string;
};

type AssessmentSessionContextValue = {
  token: string | null;
  me: MeUser | null;
  actualRole: string | null;
  isActualAdmin: boolean;
  isActualSuperAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  refreshMe: () => Promise<void>;
  clearSession: () => void;
};

type AssessmentSessionProviderProps = {
  children: ReactNode;
};

const AssessmentUserContext = createContext<
  AssessmentSessionContextValue | undefined
>(undefined);
const { assessmentsApiUrl: apiUrl } = getApiUrls();

export function AssessmentUserProvider({
  children,
}: AssessmentSessionProviderProps) {
  const [token, setToken] = useState<string | null>(() => getAuthToken());
  const [me, setMe] = useState<MeUser | null>(null);
  const [actualRole, setActualRole] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(
    () => hasPendingPreauthCodeInUrl() || Boolean(getAuthToken()),
  );
  const [error, setError] = useState<string | null>(null);
  const sessionStartedRef = useRef(false);

  const refreshMe = useCallback(async () => {
    const nextToken = getAuthToken();
    const imitationUserId = getImitationUserId();
    setToken(nextToken);

    if (!nextToken) {
      setMe(null);
      setActualRole(null);
      setIsSuperAdmin(false);
      setIsAdmin(false);
      setIsLoading(false);
      setError(
        "No session found. Open this app from Flashlight360 to start your assessment.",
      );
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      if (!apiUrl) {
        setMe(null);
        setActualRole(null);
        setIsSuperAdmin(false);
        setIsAdmin(false);
        setError("Assessments API URL is not configured.");
        return;
      }
      const assessmentUserResponse = await fetch(`${apiUrl}/user`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${nextToken}`,
          ...(imitationUserId
            ? {
                ImpersonatedUserId: imitationUserId,
              }
            : {}),
        },
      });

      const appUserRoleResponse = await fetchActualUserRole(nextToken);

      if (!assessmentUserResponse.ok) {
        if (assessmentUserResponse.status === 401) {
          clearAuthToken();
          setToken(null);
        }
        setMe(null);
        setActualRole(null);
        setIsSuperAdmin(false);
        setIsAdmin(false);
        setError(
          `Unable to load user profile (${assessmentUserResponse.status}).`,
        );
        return;
      }

      const data = (await assessmentUserResponse.json()) as {
        me: MeUser | null;
      };
      const normalizedRole = appUserRoleResponse?.trim().toLowerCase() || null;
      setMe(data?.me ?? null);
      setActualRole(normalizedRole);
      setIsSuperAdmin(normalizedRole === "super_admin");
      setIsAdmin(
        normalizedRole === "admin" || normalizedRole === "district_admin",
      );
    } catch (queryError: unknown) {
      setMe(null);
      setActualRole(null);
      setIsSuperAdmin(false);
      setIsAdmin(false);
      setError(
        queryError instanceof Error
          ? queryError.message
          : "Unable to load user profile.",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearSession = useCallback(() => {
    clearAuthToken();
    setToken(null);
    setMe(null);
    setActualRole(null);
    setIsSuperAdmin(false);
    setIsAdmin(false);
    setError(null);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (sessionStartedRef.current) {
      return;
    }
    sessionStartedRef.current = true;

    void (async () => {
      setIsLoading(true);
      const bootstrapError = await bootstrapAssessmentSessionFromUrl();
      if (bootstrapError) {
        setMe(null);
        setActualRole(null);
        setIsSuperAdmin(false);
        setIsAdmin(false);
        setToken(null);
        setError(bootstrapError);
        setIsLoading(false);
        return;
      }

      await refreshMe();
    })();
  }, [refreshMe]);

  const value = useMemo(
    () => ({
      token,
      me,
      actualRole,
      isActualAdmin: isAdmin,
      isActualSuperAdmin: isSuperAdmin,
      isLoading,
      error,
      refreshMe,
      clearSession,
    }),
    [
      token,
      me,
      actualRole,
      isAdmin,
      isSuperAdmin,
      isLoading,
      error,
      refreshMe,
      clearSession,
    ],
  );

  return (
    <AssessmentUserContext.Provider value={value}>
      {isLoading ? (
        <AssessmentLoadingState testId="assessment-session-loading" />
      ) : (
        children
      )}
    </AssessmentUserContext.Provider>
  );
}

export function useAssessmentSession(): AssessmentSessionContextValue {
  const context = useContext(AssessmentUserContext);
  if (!context) {
    throw new Error(
      "useAssessmentSession must be used within AssessmentSessionProvider.",
    );
  }
  return context;
}
